import { Flex } from "@chakra-ui/react/flex";
import { Spinner } from "@chakra-ui/react/spinner";
import PrivateRoot from "PrivateRoot";
import PublicRoot from "PublicRoot";
import ScreenSettingsProvider from "ScreenSettingsProvider";
import AbstractPrivateRoute from "components/Routes/AbstractPrivateRoute";
import AiChatsRouteGuard from "components/Routes/AiChatsRouteGuard";
import ManageInboxesRouteGuard from "components/Routes/ManageInboxesRouteGuard";
import MerchantPrivateRoute from "components/Routes/MerchantPrivateRoute";
// Eager, unlike the lazyWithRetry route components below: this is the recovery
// path for a broken session, so it must not depend on a chunk fetch that the
// same breakage could fail.
import Logout from "components/logout";
import StaleDataManager from "components/modals/stale-data-manager";
import RouteSuspense from "components/shared/RouteSuspense";
import { Provider as ChakraProvider } from "components/ui/provider";
import { isInboxPathname } from "hooks/queries/use-prefetch-conversations";
import { CancellableRequestGroupProvider } from "providers/CancellableRequestGroupProvider";
import React from "react";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
import { lazyWithRetry } from "util/lazyWithRetry";
import "./App.css";

// Lazy-loaded route components
const AutomationsPage = lazyWithRetry(() => import("components/Automations"));
const AutomationPage = lazyWithRetry(
  () => import("components/Automations/automations/AutomationPage")
);
const CallbackFail = lazyWithRetry(() => import("components/CallbackFail"));
const CallbackSuccess = lazyWithRetry(
  () => import("components/CallbackSuccess")
);
const Admin = lazyWithRetry(() => import("components/admin"));
const AdminNavigation = lazyWithRetry(
  () => import("components/admin/AdminNavigation")
);
const Agents = lazyWithRetry(() => import("components/admin/agents"));
const AI = lazyWithRetry(() => import("components/admin/ai"));
const AiAgents = lazyWithRetry(() => import("components/admin/ai-agents"));
const AdminKeyloop = lazyWithRetry(() => import("components/admin/keyloop"));
const AdminKeyloopSftpCredentials = lazyWithRetry(
  () => import("components/admin/keyloop-sftp-credentials")
);
const Broadcast = lazyWithRetry(() => import("components/admin/broadcast"));
const Merchants = lazyWithRetry(() => import("components/admin/merchants"));
const AnalyticsPage = lazyWithRetry(() => import("components/analytics"));
const AftersalesPage = lazyWithRetry(() => import("components/aftersales"));
const AiChatsPage = lazyWithRetry(() => import("components/ai-chats"));
const Calendar = lazyWithRetry(() => import("components/calendar"));
const NewCampaignForm = lazyWithRetry(
  () => import("components/campaigns/form")
);
const NewCampaignsList = lazyWithRetry(
  () => import("components/campaigns/list")
);
const Chat = lazyWithRetry(() => import("components/chat"));
const MessagesArea = lazyWithRetry(
  () => import("components/chat/shared/messages-area")
);
const ContactsPage = lazyWithRetry(() => import("components/contacts"));
const CreateContactPage = lazyWithRetry(
  () => import("components/contacts/contact-card/createContact")
);
const InstagramCallback = lazyWithRetry(
  () => import("components/facebook/callbackInstagram")
);
const FacebookMessengerCallback = lazyWithRetry(
  () => import("components/facebook/callbackMessenger")
);
const GoogleReviewsCallback = lazyWithRetry(
  () => import("components/google/callback")
);
const GoogleGmailCallback = lazyWithRetry(
  () => import("components/google/callbackGmail")
);
const GoogleLocationsOptions = lazyWithRetry(
  () => import("components/google/locations")
);
const KeyloopRepairOrdersPage = lazyWithRetry(
  () => import("components/keyloop/repair-orders")
);
const MicrosoftOutlookCallback = lazyWithRetry(
  () => import("components/microsoft/callbackOutlook")
);
const PaymentsPage = lazyWithRetry(() => import("components/payments"));
const InvoicingOptions = lazyWithRetry(
  () => import("components/payments/invoicing-options")
);
const QuickbooksPage = lazyWithRetry(
  () => import("components/payments/quickbooks")
);
const XeroPage = lazyWithRetry(() => import("components/payments/xero"));
const LeaveReview = lazyWithRetry(
  () => import("components/public-pages/LeaveReview")
);
const EmailUnsubscribePage = lazyWithRetry(
  () => import("components/public-pages/EmailUnsubscribePage")
);
const PaymentCallbackHandler = lazyWithRetry(
  () => import("components/public-pages/payments/PaymentCallbackHandler")
);
const ExternalPayment = lazyWithRetry(
  () => import("components/public-pages/payments/RequestPaymentPage")
);
const ReviewsPage = lazyWithRetry(() => import("components/reviews"));
const ReviewPage = lazyWithRetry(() => import("components/reviews/ReviewPage"));
const ContactDetailsRoute = lazyWithRetry(
  () => import("components/contacts/ContactDetailsRoute")
);
const StripeCallback = lazyWithRetry(
  () => import("components/stripe/callback")
);
const UserSettingsWithProvider = lazyWithRetry(
  () => import("components/user-settings")
);
const AIAssistantPage = lazyWithRetry(
  () => import("components/user-settings/AIAssistant/AIAssistantPage")
);
const CreateEvalCasePage = lazyWithRetry(
  () =>
    import(
      "components/user-settings/AIAssistant/AIAssistantEvals/CreateEvalCasePage"
    )
);
const EditEvalCasePage = lazyWithRetry(
  () =>
    import(
      "components/user-settings/AIAssistant/AIAssistantEvals/EditEvalCasePage"
    )
);
const EvalRunPage = lazyWithRetry(
  () =>
    import("components/user-settings/AIAssistant/AIAssistantEvals/EvalRunPage")
);
const AIAssistants = lazyWithRetry(
  () => import("components/user-settings/AIAssistants")
);
const AccountOverview = lazyWithRetry(
  () => import("components/user-settings/AccountOverview/AccountOverview")
);
const CallScriptsSettings = lazyWithRetry(
  () =>
    import("components/user-settings/CallScriptsSettings/CallScriptsSettings")
);
const CreateCallScript = lazyWithRetry(
  () =>
    import("components/user-settings/CallScriptsSettings/create-call-script")
);
const EditCallScript = lazyWithRetry(
  () => import("components/user-settings/CallScriptsSettings/edit-call-script")
);
const Channels = lazyWithRetry(
  () => import("components/user-settings/Channels")
);
const ContactAttributeSettings = lazyWithRetry(() =>
  import("components/user-settings/ContactAttributes").then((m) => ({
    default: m.ContactAttributeSettings,
  }))
);
const CreateContactAttribute = lazyWithRetry(
  () =>
    import(
      "components/user-settings/ContactAttributes/create-contact-attribute"
    )
);
const EditContactAttribute = lazyWithRetry(
  () =>
    import("components/user-settings/ContactAttributes/edit-contact-attribute")
);
const Files = lazyWithRetry(() => import("components/user-settings/Files"));
const ImportHistory = lazyWithRetry(
  () => import("components/user-settings/ImportHistory")
);
const InboxSettings = lazyWithRetry(
  () => import("components/user-settings/InboxSettings")
);
const CreateInbox = lazyWithRetry(
  () => import("components/user-settings/InboxSettings/CreateInbox")
);
const EditInbox = lazyWithRetry(
  () => import("components/user-settings/InboxSettings/EditInbox")
);
const TagSettings = lazyWithRetry(
  () => import("components/user-settings/TagSettings")
);
const CreateTag = lazyWithRetry(
  () => import("components/user-settings/TagSettings/create-tag")
);
const NewIntegrations = lazyWithRetry(
  () => import("components/user-settings/Integrations")
);
const WhatsappIntegration = lazyWithRetry(
  () => import("components/user-settings/Integrations/WhatsappIntegration")
);
const Keyloop = lazyWithRetry(
  () => import("components/user-settings/Integrations/keyloop")
);
const AutoTraderConnections = lazyWithRetry(
  () => import("components/user-settings/Integrations/autotrader")
);
const LeadEmailAddresses = lazyWithRetry(
  () => import("components/user-settings/Integrations/lead-emails")
);
const RolePermissions = lazyWithRetry(
  () => import("components/user-settings/RolePermissions")
);
const TeamMemberSettings = lazyWithRetry(
  () => import("components/user-settings/TeamMemberSettings")
);
const TwoFactorSettings = lazyWithRetry(
  () => import("components/user-settings/TwoFactorSettings/TwoFactorSettings")
);
const TeamMemberForm = lazyWithRetry(
  () => import("components/user-settings/TeamMemberSettings/TeamMemberForm")
);
const TemplateSettings = lazyWithRetry(
  () => import("components/user-settings/TemplateSettings/TemplateSettings")
);
const TemplateView = lazyWithRetry(
  () => import("components/user-settings/TemplateSettings/TemplateView")
);
const CreateWhatsappTemplate = lazyWithRetry(
  () =>
    import("components/user-settings/TemplateSettings/create-whatsapp-template")
);
const EditWhatsappTemplate = lazyWithRetry(
  () =>
    import("components/user-settings/TemplateSettings/edit-whatsapp-template")
);
const ViewWhatsappTemplate = lazyWithRetry(
  () =>
    import("components/user-settings/TemplateSettings/view-whatsapp-template")
);
const EmailDomainSettings = lazyWithRetry(
  () =>
    import("components/user-settings/EmailDomainSettings/EmailDomainSettings")
);
const EmailDomainDetailPage = lazyWithRetry(
  () =>
    import("components/user-settings/EmailDomainSettings/EmailDomainDetailPage")
);
const EmailTemplateEditor = lazyWithRetry(
  () =>
    import(
      "components/user-settings/EmailTemplateSettings/editor/EmailTemplateEditor"
    )
);
// Start downloading the Chat chunk immediately on inbox landings instead of
// waiting for React to reach the lazy boundary. PrivateRoute holds a spinner
// until /merchants/:id and /merchants/:id/agents resolve (~700ms), and without
// this the chunk fetch only starts AFTER that wait — a fully sequential network
// hop on the LCP critical path. Preloading here overlaps the download and
// module evaluation with the API wait; Vite deduplicates the import with the
// lazy() factory, so the route picks up the same in-flight promise.
const preloadChatOnInboxLanding = () => {
  if (isInboxPathname(window.location.pathname)) {
    Chat.preload().catch(() => {});
  }
};

preloadChatOnInboxLanding();

// Module-level singleton so it can be driven imperatively from outside the
// React tree: Auth0's onRedirectCallback lives above RouterProvider (no
// useNavigate there), so it calls router.navigate() for a client-side
// post-login redirect instead of a full page reload. This was already a
// useMemo([]) singleton inside App() — module scope just exposes it.
export const router = createBrowserRouter([
  {
    path: "/",
    Component: PrivateRoot,
    children: [
      {
        path: ":merchantId",
        element: <MerchantPrivateRoute />,
        children: [
          {
            path: "inbox",
            element: (
              <RouteSuspense>
                <Chat />
              </RouteSuspense>
            ),
            children: [
              {
                path: ":conversationId",
                element: (
                  <RouteSuspense>
                    <MessagesArea />
                  </RouteSuspense>
                ),
              },
            ],
          },
          {
            path: "reviews",
            element: (
              <RouteSuspense>
                <ReviewsPage />
              </RouteSuspense>
            ),
            children: [
              {
                path: ":reviewId",
                element: (
                  <RouteSuspense>
                    <ReviewPage />
                  </RouteSuspense>
                ),
              },
            ],
          },
          {
            path: "ai-chats",
            element: (
              <AiChatsRouteGuard redirectTo="inbox">
                <RouteSuspense>
                  <AiChatsPage />
                </RouteSuspense>
              </AiChatsRouteGuard>
            ),
          },
          {
            path: "ai-chats/:threadId",
            element: (
              <AiChatsRouteGuard redirectTo="inbox">
                <RouteSuspense>
                  <AiChatsPage />
                </RouteSuspense>
              </AiChatsRouteGuard>
            ),
          },
          {
            path: "payments",
            element: (
              <RouteSuspense>
                <PaymentsPage />
              </RouteSuspense>
            ),
          },
          {
            path: "keyloop/repair-orders",
            element: (
              <RouteSuspense>
                <KeyloopRepairOrdersPage />
              </RouteSuspense>
            ),
          },
          {
            path: "calendar",
            element: (
              <RouteSuspense>
                <Calendar />
              </RouteSuspense>
            ),
          },
          {
            path: "analytics",
            element: (
              <RouteSuspense>
                <AnalyticsPage />
              </RouteSuspense>
            ),
          },
          {
            path: "aftersales",
            element: (
              <RouteSuspense>
                <AftersalesPage />
              </RouteSuspense>
            ),
          },
          {
            path: "invoicing",
            element: (
              <RouteSuspense>
                <InvoicingOptions />
              </RouteSuspense>
            ),
          },
          {
            path: "campaigns/new",
            element: (
              <RouteSuspense>
                <NewCampaignForm />
              </RouteSuspense>
            ),
          },
          {
            path: "campaigns/edit/:campaignId",
            element: (
              <RouteSuspense>
                <NewCampaignForm />
              </RouteSuspense>
            ),
          },
          {
            path: "campaigns",
            element: (
              <RouteSuspense>
                <NewCampaignsList />
              </RouteSuspense>
            ),
          },
          {
            path: "automations",
            element: (
              <RouteSuspense>
                <AutomationsPage />
              </RouteSuspense>
            ),
          },
          {
            path: "settings",
            element: (
              <RouteSuspense>
                <UserSettingsWithProvider />
              </RouteSuspense>
            ),
            children: [
              {
                index: true,
                element: (
                  <RouteSuspense>
                    <AccountOverview />
                  </RouteSuspense>
                ),
              },
              {
                path: "channels",
                element: (
                  <RouteSuspense>
                    <Channels />
                  </RouteSuspense>
                ),
              },
              {
                path: "ai_assistants",
                element: (
                  <RouteSuspense>
                    <AIAssistants />
                  </RouteSuspense>
                ),
              },
              {
                path: "integrations",
                element: (
                  <RouteSuspense>
                    <NewIntegrations />
                  </RouteSuspense>
                ),
                children: [
                  {
                    path: "keyloop",
                    element: (
                      <RouteSuspense>
                        <Keyloop />
                      </RouteSuspense>
                    ),
                  },
                  {
                    path: "autotrader",
                    element: (
                      <RouteSuspense>
                        <AutoTraderConnections />
                      </RouteSuspense>
                    ),
                  },
                  {
                    path: "lead-emails",
                    element: (
                      <RouteSuspense>
                        <LeadEmailAddresses />
                      </RouteSuspense>
                    ),
                  },
                ],
              },
              {
                path: "account",
                element: (
                  <RouteSuspense>
                    <AccountOverview />
                  </RouteSuspense>
                ),
              },
              {
                path: "files",
                element: (
                  <RouteSuspense>
                    <Files />
                  </RouteSuspense>
                ),
              },
              {
                path: "import-history",
                element: (
                  <RouteSuspense>
                    <ImportHistory />
                  </RouteSuspense>
                ),
              },
              {
                path: "import-history/:fileId",
                element: (
                  <RouteSuspense>
                    <ImportHistory />
                  </RouteSuspense>
                ),
              },
              {
                path: "templates",
                element: (
                  <RouteSuspense>
                    <TemplateSettings />
                  </RouteSuspense>
                ),
                children: [
                  {
                    path: "whatsapp/:templateId",
                    element: (
                      <RouteSuspense>
                        <ViewWhatsappTemplate />
                      </RouteSuspense>
                    ),
                  },
                ],
              },
              {
                path: "call-scripts",
                element: (
                  <RouteSuspense>
                    <CallScriptsSettings />
                  </RouteSuspense>
                ),
              },
              {
                path: "teammates",
                element: (
                  <RouteSuspense>
                    <TeamMemberSettings />
                  </RouteSuspense>
                ),
              },
              {
                path: "inboxes",
                element: (
                  <ManageInboxesRouteGuard redirectTo="settings">
                    <RouteSuspense>
                      <InboxSettings />
                    </RouteSuspense>
                  </ManageInboxesRouteGuard>
                ),
              },
              {
                path: "permissions",
                element: (
                  <RouteSuspense>
                    <RolePermissions />
                  </RouteSuspense>
                ),
              },
              {
                path: "security",
                element: (
                  <RouteSuspense>
                    <TwoFactorSettings />
                  </RouteSuspense>
                ),
              },
              {
                path: "attributes",
                element: (
                  <RouteSuspense>
                    <ContactAttributeSettings />
                  </RouteSuspense>
                ),
                children: [
                  {
                    path: "create",
                    element: (
                      <RouteSuspense>
                        <CreateContactAttribute />
                      </RouteSuspense>
                    ),
                  },
                  {
                    path: ":attributeId",
                    element: (
                      <RouteSuspense>
                        <EditContactAttribute />
                      </RouteSuspense>
                    ),
                  },
                ],
              },
              {
                path: "tags",
                element: (
                  <RouteSuspense>
                    <TagSettings />
                  </RouteSuspense>
                ),
                children: [
                  {
                    path: "create",
                    element: (
                      <RouteSuspense>
                        <CreateTag />
                      </RouteSuspense>
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: "automations/:automationId",
            element: (
              <RouteSuspense>
                <AutomationPage />
              </RouteSuspense>
            ),
          },
          {
            path: "automations/new",
            element: (
              <RouteSuspense>
                <AutomationPage />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/integrations/whatsapp",
            element: (
              <RouteSuspense>
                <WhatsappIntegration />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/email-domains",
            element: (
              <RouteSuspense>
                <EmailDomainSettings />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/email-domains/:domainId",
            element: (
              <RouteSuspense>
                <EmailDomainDetailPage />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/templates/email/create",
            element: (
              <RouteSuspense>
                <EmailTemplateEditor />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/templates/email/edit/:templateName",
            element: (
              <RouteSuspense>
                <EmailTemplateEditor />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/templates/create",
            element: (
              <RouteSuspense>
                <TemplateView />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/call-scripts/create",
            element: (
              <RouteSuspense>
                <CreateCallScript />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/call-scripts/edit/:voiceScriptId",
            element: (
              <RouteSuspense>
                <EditCallScript />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/templates/whatsapp/create",
            element: (
              <RouteSuspense>
                <CreateWhatsappTemplate />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/templates/whatsapp/edit/:templateId",
            element: (
              <RouteSuspense>
                <EditWhatsappTemplate />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/templates/:templateId",
            element: (
              <RouteSuspense>
                <TemplateView />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/inboxes/create",
            element: (
              <ManageInboxesRouteGuard redirectTo="settings">
                <RouteSuspense>
                  <CreateInbox />
                </RouteSuspense>
              </ManageInboxesRouteGuard>
            ),
          },
          {
            path: "settings/inboxes/edit/:inboxId",
            element: (
              <ManageInboxesRouteGuard redirectTo="settings">
                <RouteSuspense>
                  <EditInbox />
                </RouteSuspense>
              </ManageInboxesRouteGuard>
            ),
          },
          {
            path: "settings/teammates/:memberId",
            element: (
              <RouteSuspense>
                <TeamMemberForm />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/ai_assistants/:assistantId",
            element: (
              <RouteSuspense>
                <AIAssistantPage />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/ai_assistants/create",
            element: (
              <RouteSuspense>
                <AIAssistantPage />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/ai_assistants/:assistantId/eval-cases/create",
            element: (
              <RouteSuspense>
                <CreateEvalCasePage />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/ai_assistants/:assistantId/eval-cases/:evalCaseId/edit",
            element: (
              <RouteSuspense>
                <EditEvalCasePage />
              </RouteSuspense>
            ),
          },
          {
            path: "settings/ai_assistants/:assistantId/eval-runs/:runId",
            element: (
              <RouteSuspense>
                <EvalRunPage />
              </RouteSuspense>
            ),
          },
          {
            path: "contacts",
            element: (
              <RouteSuspense>
                <ContactsPage />
              </RouteSuspense>
            ),
            children: [
              {
                path: ":contactId",
                element: (
                  <RouteSuspense>
                    <ContactDetailsRoute />
                  </RouteSuspense>
                ),
              },
            ],
          },
          {
            path: "contacts/create",
            element: (
              <RouteSuspense>
                <CreateContactPage />
              </RouteSuspense>
            ),
          },
          {
            path: "admin",
            element: (
              <RouteSuspense>
                <Admin />
              </RouteSuspense>
            ),
            children: [
              {
                index: true,
                element: (
                  <RouteSuspense>
                    <AdminNavigation />
                  </RouteSuspense>
                ),
              },
              {
                path: "merchants",
                element: (
                  <RouteSuspense>
                    <Merchants />
                  </RouteSuspense>
                ),
              },
              {
                path: "agents",
                element: (
                  <RouteSuspense>
                    <Agents />
                  </RouteSuspense>
                ),
              },
              {
                path: "ai",
                element: (
                  <RouteSuspense>
                    <AI />
                  </RouteSuspense>
                ),
              },
              {
                path: "ai-agents",
                element: (
                  <RouteSuspense>
                    <AiAgents />
                  </RouteSuspense>
                ),
              },
              {
                path: "broadcast",
                element: (
                  <RouteSuspense>
                    <Broadcast />
                  </RouteSuspense>
                ),
              },
              {
                path: "keyloop",
                element: (
                  <RouteSuspense>
                    <AdminKeyloop />
                  </RouteSuspense>
                ),
              },
              {
                path: "keyloop-sftp-credentials",
                element: (
                  <RouteSuspense>
                    <AdminKeyloopSftpCredentials />
                  </RouteSuspense>
                ),
              },
            ],
          },
          {
            index: true,
            element: (
              <RouteSuspense>
                <Chat />
              </RouteSuspense>
            ),
          },
        ],
      },
      {
        index: true,
        element: (
          <AbstractPrivateRoute>
            <RouteSuspense>
              <Chat />
            </RouteSuspense>
          </AbstractPrivateRoute>
        ),
      },
      {
        path: "reviews/google/locations",
        element: (
          <AbstractPrivateRoute>
            <RouteSuspense>
              <GoogleLocationsOptions />
            </RouteSuspense>
          </AbstractPrivateRoute>
        ),
      },
      {
        path: "settings/integrations/keyloop",
        element: (
          <AbstractPrivateRoute>
            <RouteSuspense>
              <Keyloop />
            </RouteSuspense>
          </AbstractPrivateRoute>
        ),
      },
      {
        path: "reviews/google/callback",
        element: (
          <AbstractPrivateRoute>
            <RouteSuspense>
              <GoogleReviewsCallback />
            </RouteSuspense>
          </AbstractPrivateRoute>
        ),
      },
      {
        path: "google/gmail/callback",
        element: (
          <AbstractPrivateRoute>
            <RouteSuspense>
              <GoogleGmailCallback />
            </RouteSuspense>
          </AbstractPrivateRoute>
        ),
      },
      {
        path: "microsoft/callback",
        element: (
          <AbstractPrivateRoute>
            <RouteSuspense>
              <MicrosoftOutlookCallback />
            </RouteSuspense>
          </AbstractPrivateRoute>
        ),
      },
      {
        path: "stripe/callback",
        element: (
          <AbstractPrivateRoute>
            <RouteSuspense>
              <StripeCallback />
            </RouteSuspense>
          </AbstractPrivateRoute>
        ),
      },
      {
        path: "facebook/callback/messenger",
        element: (
          <AbstractPrivateRoute>
            <RouteSuspense>
              <FacebookMessengerCallback />
            </RouteSuspense>
          </AbstractPrivateRoute>
        ),
      },
      {
        path: "facebook/callback/instagram",
        element: (
          <AbstractPrivateRoute>
            <RouteSuspense>
              <InstagramCallback />
            </RouteSuspense>
          </AbstractPrivateRoute>
        ),
      },
      {
        path: "/xero",
        element: (
          <AbstractPrivateRoute>
            <RouteSuspense>
              <XeroPage />
            </RouteSuspense>
          </AbstractPrivateRoute>
        ),
      },
      {
        path: "/quickbooks",
        element: (
          <AbstractPrivateRoute>
            <RouteSuspense>
              <QuickbooksPage />
            </RouteSuspense>
          </AbstractPrivateRoute>
        ),
      },
    ],
  },
  {
    path: "/public",
    Component: PublicRoot,
    children: [
      {
        path: "pay/:payment_id",
        element: (
          <RouteSuspense>
            <ExternalPayment />
          </RouteSuspense>
        ),
      },
      {
        path: "review/:review_id",
        element: (
          <RouteSuspense>
            <LeaveReview />
          </RouteSuspense>
        ),
      },
      {
        path: "payments/callback",
        element: (
          <RouteSuspense>
            <PaymentCallbackHandler />
          </RouteSuspense>
        ),
      },
      {
        path: "callback/success",
        element: (
          <RouteSuspense>
            <CallbackSuccess />
          </RouteSuspense>
        ),
      },
      {
        path: "callback/fail",
        element: (
          <RouteSuspense>
            <CallbackFail />
          </RouteSuspense>
        ),
      },
      {
        path: "emails/unsubscribe",
        element: (
          <RouteSuspense>
            <EmailUnsubscribePage />
          </RouteSuspense>
        ),
      },
    ],
  },
  {
    // Deliberately outside PrivateRoot: that wrapper is withAuthenticationRequired,
    // which would bounce an expired session to the Auth0 login screen instead of
    // ending it. Reached on every 401 via src/services/http.ts.
    path: "/logout",
    element: <Logout />,
  },
  {
    path: "*",
    element: (
      <PrivateRoot>
        <AbstractPrivateRoute />
      </PrivateRoot>
    ),
    errorElement: <Navigate to="/inbox" replace={true} />,
  },
]);

function App() {
  return (
    <ChakraProvider>
      <ScreenSettingsProvider>
        <CancellableRequestGroupProvider>
          <RouterProvider router={router} />
          <StaleDataManager />
        </CancellableRequestGroupProvider>
      </ScreenSettingsProvider>
    </ChakraProvider>
  );
}

export default App;
